import { Link } from 'react-router-dom'
import { Linkedin, Twitter, Facebook, Mail, ArrowRight } from 'lucide-react'
import { footerNav } from '../../data/nav'
import { Container } from '../ui/Container'
import logo from '../../assets/polakis-logo.png'

function FooterColumn({ title, links }: { title: string; links: { label: string; href: string }[] }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-ink-400">{title}</h4>
      <ul className="mt-4 space-y-2.5">
        {links.map((link) => (
          <li key={link.label}>
            <Link to={link.href} className="text-sm text-ink-300 transition-colors hover:text-white">
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function Footer() {
  return (
    <footer className="bg-ink-900">
      <Container className="py-16 lg:py-20">
        <div className="grid grid-cols-2 gap-10 lg:grid-cols-6">
          <div className="col-span-2">
            <Link to="/" className="inline-flex items-center">
              <img src={logo} alt="Polakis Technologies" className="h-10 w-auto brightness-0 invert" />
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-ink-300">
              Enterprise software products for healthcare, insurance, fleet, logistics and property organizations across Africa and beyond.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[
                { icon: Linkedin, href: 'https://linkedin.com', label: 'LinkedIn' },
                { icon: Twitter, href: 'https://twitter.com', label: 'Twitter' },
                { icon: Facebook, href: 'https://facebook.com', label: 'Facebook' },
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-ink-300 transition-colors hover:border-teal-400/40 hover:text-teal-300"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <FooterColumn title="Products" links={footerNav.products} />
          <FooterColumn title="Industries" links={footerNav.industries} />
          <FooterColumn title="Solutions" links={footerNav.solutions} />
          <FooterColumn title="Company" links={footerNav.company} />
        </div>

        <div className="mt-16 flex flex-col gap-6 border-t border-white/10 pt-10 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h4 className="text-sm font-semibold text-white">Get product updates</h4>
            <p className="mt-1 text-sm text-ink-400">Occasional emails on new features and industry insight. No spam.</p>
          </div>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="flex w-full max-w-sm items-center gap-2 rounded-full border border-white/10 bg-white/5 p-1.5 pl-4"
          >
            <Mail className="h-4 w-4 shrink-0 text-ink-400" />
            <input
              type="email"
              required
              placeholder="you@company.com"
              className="w-full bg-transparent text-sm text-white placeholder:text-ink-500 focus:outline-none"
            />
            <button
              type="submit"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-400 text-ink-900 transition-transform hover:scale-105"
              aria-label="Subscribe"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-white/10 pt-8 text-xs text-ink-500 sm:flex-row sm:items-center sm:justify-between">
          <p>&copy; {new Date().getFullYear()} Polakis Technologies. All rights reserved.</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {footerNav.legal.map((link) => (
              <Link key={link.label} to={link.href} className="hover:text-ink-300">
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </Container>
    </footer>
  )
}
